// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, AlertCircle, Loader2, UserPlus, LogIn, Calendar, MapPin, Users } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { parseLocalDate } from '@/utils/formatters';

interface InvitationData {
  id: string;
  coach_name: string;
  coach_email: string;
  status: string;
  team_id: string;
  event_id: string;
  registration_id: string;
  team?: {
    id: string;
    name: string;
  };
  event?: {
    id: string;
    name: string;
    start_date: string;
    end_date: string;
    venue_name: string;
    venue_city: string;
    venue_state: string;
  };
}

const AcceptCoachInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, profile, isLoading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);

  const invitationId =
    searchParams.get('id') ??
    searchParams.get('invitation_id') ??
    searchParams.get('invite_code');

  useEffect(() => {
    if (!invitationId) {
      setError('Invalid invitation link. No invitation ID provided.');
      setLoading(false);
      return;
    }

    fetchInvitationData();
  }, [invitationId]);

  useEffect(() => {
    // Once auth is loaded and we have invitation data, check if user is logged in
    if (!authLoading && invitation && user && profile) {
      handleLoggedInUser();
    }
  }, [authLoading, invitation, user, profile]);

  const fetchInvitationData = async () => {
    try {
      setLoading(true);
      console.log('🔍 [ACCEPT-INVITE] Fetching invitation data for ID:', invitationId);

      const { data: inviteData, error: inviteError } = await invokeEdgeFunction('get-coach-invitation', {
        body: { invitation_id: invitationId }
      });

      if (inviteError || !inviteData) {
        console.error('❌ [ACCEPT-INVITE] Invitation fetch error:', inviteError);
        setError('Invalid invitation link. This invitation does not exist or has expired.');
        setLoading(false);
        return;
      }

      console.log('✅ [ACCEPT-INVITE] Invitation found:', inviteData);

      setInvitation(inviteData as InvitationData);
      setLoading(false);
    } catch (err) {
      console.error('❌ [ACCEPT-INVITE] Exception:', err);
      setError('An unexpected error occurred while loading the invitation.');
      setLoading(false);
    }
  };

  const handleLoggedInUser = async () => {
    if (!invitation || !user || !profile) return;

    // Check if invitation is already accepted
    if (invitation.status === 'accepted') {
      setSuccess(true);
      toast.success('This invitation has already been accepted!');
      return;
    }

    // Auto-accept if the logged-in user email matches the invitation
    if (profile.email?.toLowerCase() === invitation.coach_email?.toLowerCase()) {
      await acceptInvitation();
    }
  };

  const acceptInvitation = async () => {
    if (!invitation || !user) return;

    try {
      setProcessing(true);
      console.log('🔄 [ACCEPT-INVITE] Accepting invitation:', invitation.id);

      // Call the accept-coach-invitation edge function
      const { data, error: acceptError } = await invokeEdgeFunction('accept-coach-invitation', {
        body: { invitation_id: invitation.id }
      });

      if (acceptError) {
        console.error('❌ [ACCEPT-INVITE] Accept error:', acceptError);
        toast.error('Failed to accept invitation. Please try again.');
        setProcessing(false);
        return;
      }

      console.log('✅ [ACCEPT-INVITE] Invitation accepted:', data);
      setSuccess(true);
      toast.success('Invitation accepted! You can now manage your team.');

      // Redirect to club coach events page after a short delay
      setTimeout(() => {
        navigate('/club-coach/events');
      }, 2000);

    } catch (err) {
      console.error('❌ [ACCEPT-INVITE] Exception:', err);
      toast.error('Failed to accept invitation. Please try again.');
      setProcessing(false);
    }
  };

  const handleLogin = () => {
    // Store invitation ID to auto-accept after login
    sessionStorage.setItem('pending_invitation_id', invitationId || '');
    navigate(`/auth/login?redirect=/accept-coach-invitation?id=${invitationId}`);
  };

  const handleSignup = () => {
    // Store invitation ID to auto-accept after signup
    sessionStorage.setItem('pending_invitation_id', invitationId || '');
    // Pass role=club_coach so the user is NOT created as an athlete
    navigate(`/auth/register?redirect=/accept-coach-invitation?id=${invitationId}&email=${encodeURIComponent(invitation?.coach_email || '')}&role=club_coach`);
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Loading invitation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Invalid Invitation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button className="w-full mt-4" onClick={() => navigate('/')}>
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              Invitation Accepted!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              You have successfully accepted the invitation to coach{' '}
              <strong>{invitation?.team?.name}</strong> at{' '}
              <strong>{invitation?.event?.name}</strong>.
            </p>
            <Button className="w-full" onClick={() => navigate('/club-coach/events')}>
              Go to My Events
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Coach Invitation
          </CardTitle>
          <CardDescription>
            You've been invited to coach a team
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invitation Details */}
          <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Team</p>
              <p className="font-semibold">{invitation?.team?.name || 'Team'}</p>
            </div>
            
            {invitation?.event && (
              <>
                <div>
                  <p className="text-sm text-muted-foreground">Event</p>
                  <p className="font-semibold">{invitation.event.name}</p>
                </div>
                
                {(invitation.event.start_date || invitation.event.end_date) && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {invitation.event.start_date && parseLocalDate(invitation.event.start_date).toLocaleDateString()}
                      {invitation.event.end_date && ` - ${parseLocalDate(invitation.event.end_date).toLocaleDateString()}`}
                    </span>
                  </div>
                )}
                
                {(invitation.event.venue_name || invitation.event.venue_city || invitation.event.venue_state) && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>
                      {[invitation.event.venue_name, invitation.event.venue_city, invitation.event.venue_state]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Action Buttons */}
          {user && profile ? (
            // User is logged in
            <div className="space-y-3">
              {processing ? (
                <Button disabled className="w-full">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Accepting Invitation...
                </Button>
              ) : (
                <Button className="w-full" size="lg" onClick={acceptInvitation}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Accept Invitation
                </Button>
              )}
            </div>
          ) : (
            // User is not logged in
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground text-center">
                To accept this invitation, please log in or create an account:
              </p>
              
              <Button className="w-full" size="lg" onClick={handleLogin}>
                <LogIn className="h-4 w-4 mr-2" />
                Log In to Accept
              </Button>
              
              <Button variant="outline" className="w-full" size="lg" onClick={handleSignup}>
                <UserPlus className="h-4 w-4 mr-2" />
                Create Account
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptCoachInvitation;